import { memo, type ReactNode } from 'react'

import { Group, Text } from '@mantine/core'

import { getTokenNames, type TokenSymbol } from '@repo/common/queries/products'

import { TokenImage, type TokenImageSlug } from '../TokenImage'
import { TokenPair } from '../TokenPair'
import { TokenVault } from '../TokenVault'

export type TokenRenderType = 'token' | 'fund' | 'lp' | 'vault'
export type TokenRenderProps = {
  type?: TokenRenderType
  symbol: TokenSymbol | [TokenSymbol, TokenSymbol]
}

export { type TokenImageSlug }

type RenderSize = `var(--mantine-font-size-${string})` | `${number}px`
export const TokenRender = memo<
  TokenRenderProps & {
    size?: RenderSize
  }
  // eslint-disable-next-line complexity
>(function TokenRender({
  size = '80px',
  symbol,
  type = symbol instanceof Array ? undefined : getTokenNames(symbol).type,
}) {
  if (symbol instanceof Array) {
    return (
      <TokenPair
        first={getTokenNames(symbol[0]).image_slug}
        second={getTokenNames(symbol[1]).image_slug}
        size={size}
      />
    )
  }

  switch (type) {
    case 'token':
    case 'fund':
      return (
        <TokenImage image_slug={getTokenNames(symbol).image_slug} size={size} />
      )
    case 'vault':
      return (
        <TokenVault image_slug={getTokenNames(symbol).image_slug} size={size} />
      )
  }
})

const sizes = {
  md: { token: 'var(--mantine-font-size-lg)', font: 'md' },
  lg: { token: 'var(--mantine-font-size-xl1)', font: 'lg' },
} as const

export const TokenAndSymbolRender = memo<
  TokenRenderProps & {
    size?: keyof typeof sizes
    opacity?: number
  }
>(function TokenAndSymbolRender(props) {
  return <TokenAndTextRender {...props}>{props.symbol}</TokenAndTextRender>
})

export const TokenAndTextRender = memo<
  TokenRenderProps & {
    size?: keyof typeof sizes
    opacity?: number
    children: ReactNode
  }
>(function TokenAndTextRender({ symbol, children, size = 'md', opacity }) {
  return (
    <Group gap="0.5ex" align="center" wrap="nowrap">
      <TokenRender size={sizes[size].token} symbol={symbol} />
      <Text size={sizes[size].font} lh="1" opacity={opacity}>
        {children}
      </Text>
    </Group>
  )
})
